import React, { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Lock, ArrowLeft, Plus, Save, Trash2, Sparkles } from "lucide-react";
import { Link } from "react-router-dom";
import BrandLogo from "@/components/BrandLogo/BrandLogo";
import "./Admin.css";

/* ----------------------------
   STATIC "API" (no backend)
----------------------------- */
const deepClone = (x) => {
  try {
    // eslint-disable-next-line no-undef
    return structuredClone(x);
  } catch {
    return JSON.parse(JSON.stringify(x));
  }
};

const nowIso = () => new Date().toISOString();
const addDaysIso = (d) => new Date(Date.now() + d * 86400000).toISOString();

const STATIC_DB = {
  siteContent: {
    hero_overline: "A members-led society for virtual events",
    hero_headline_1: "Conversations",
    hero_headline_2: "worth showing up for.",
    hero_body:
      "vmeetio runs virtual events as a set of master sessions and small, curated tables. Move between topics. Build a trusted circle.",
    hero_quote_text:
      "The closest thing to a great dinner conversation — except it's on your calendar.",
    hero_quote_attribution: "Ava Moreno · Operator · Series B SaaS",
    today_max_events: 6,
    onboarding_completed: false,
    value_strip: [],
    how_it_works: [],
    faq: [],
    testimonials: [],
    sponsor_tiers: [],
    hero_image: "",
    logo_url: "",
    membership_image: "",
    method_headline_1: "Master sessions.",
    method_headline_2: "Small tables.",
    method_headline_3: "Conversations worth showing up for.",
    method_body:
      "Every vmeetio event is structured as pre-set master sessions, with small tables of 6–8 people anchored by a moderator.",
    brands_overline: "Brands hosting events",
    brands_headline_1: "The brands convening",
    brands_headline_2: "this month's tables.",
    circles_headline_1: "Find the people you'd actually",
    circles_headline_2: "email at midnight.",
  },

  events: [
    {
      id: 1,
      title: "AI After Hours",
      description:
        "Signal over noise: inference economics, evals, and what actually ships.",
      start_time: addDaysIso(1),
      end_time: addDaysIso(1),
      timezone: "UTC",
      cover:
        "https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=1800&q=80",
      host: "vmeetio",
      registered: 184,
      tables_count: 3,
      tags: ["AI", "Research", "Scale"],
      images: [],
      is_open_for_sponsorship: true,
    },
    {
      id: 2,
      title: "Climate Capital Salon",
      description:
        "Founder–investor tables focused on deployment, hardware, and financing.",
      start_time: addDaysIso(7),
      end_time: addDaysIso(7),
      timezone: "UTC",
      cover:
        "https://images.unsplash.com/photo-1509395176047-4a66953fd231?auto=format&fit=crop&w=1800&q=80",
      host: "vmeetio",
      registered: 122,
      tables_count: 2,
      tags: ["Climate", "Capital", "Founders"],
      images: [],
      is_open_for_sponsorship: true,
    },
  ],

  sessions: [
    {
      id: 1,
      event_id: 1,
      title: "Master session",
      description: "Key framing and context.",
      start_time: addDaysIso(1),
      end_time: addDaysIso(1),
      speaker: "Host",
    },
    {
      id: 2,
      event_id: 1,
      title: "Tables open",
      description: "Join a table and move freely.",
      start_time: addDaysIso(1),
      end_time: addDaysIso(1),
      speaker: "",
    },
    {
      id: 3,
      event_id: 2,
      title: "Opening",
      description: "What's in the room + agenda.",
      start_time: addDaysIso(7),
      end_time: addDaysIso(7),
      speaker: "Moderator",
    },
  ],

  tables: [
    {
      id: 101,
      event_id: 1,
      topic: "Open-weight inference economics (real numbers)",
      description:
        "Latency, cost, and throughput tradeoffs for open weights in production.",
      moderator: "Eliza Marston",
      moderator_avatar:
        "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80",
      seats: 8,
      taken: 6,
      circle: "AI Research",
      is_live: true,
      attendees: [
        { name: "Mei L.", email: "mei@example.com" },
        { name: "Ariel J.", email: "ariel@example.com" },
        { name: "Soren V.", email: "soren@example.com" },
        { name: "Hana A.", email: "hana@example.com" },
        { name: "Jordan T.", email: "jordan@example.com" },
        { name: "Dr. Imani C.", email: "imani@example.com" },
      ],
    },
    {
      id: 102,
      event_id: 1,
      topic: "Evals that catch regressions before prod does",
      description:
        "How teams ship safely: eval harnesses, red-teaming, and monitoring.",
      moderator: "Dr. Imani Cole",
      moderator_avatar:
        "https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=600&q=80",
      seats: 8,
      taken: 8,
      circle: "Founders",
      is_live: true,
      attendees: [
        { name: "Sarah K.", email: "sarah@example.com" },
        { name: "Noah P.", email: "noah@example.com" },
        { name: "Riya S.", email: "riya@example.com" },
        { name: "Sam D.", email: "sam@example.com" },
        { name: "Leo M.", email: "leo@example.com" },
        { name: "Anya R.", email: "anya@example.com" },
        { name: "Chris T.", email: "chris@example.com" },
        { name: "Dana W.", email: "dana@example.com" },
      ],
    },
    {
      id: 201,
      event_id: 2,
      topic: "Term sheets, board dynamics, and flat rounds",
      description:
        "What actually happens in the room when pricing goes sideways.",
      moderator: "Eliza Marston",
      moderator_avatar:
        "https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=600&q=80",
      seats: 8,
      taken: 2,
      circle: "Climate",
      is_live: false,
      attendees: [
        { name: "Guest 1", email: "guest1@example.com" },
        { name: "Guest 2", email: "guest2@example.com" },
      ],
    },
  ],

  brands: [
    {
      company: "Greentide",
      value_prop: "Climate ops & deployment playbooks.",
      website: "https://example.com",
      logo: "",
      contact_email: "",
      phone: "",
      linkedin: "",
      video_url: "",
      pdf_url: "",
      pdf_caption: "",
      overview: "",
    },
    {
      company: "Anyon AI",
      value_prop: "Open-weight inference economics.",
      website: "https://example.com",
      logo: "",
      contact_email: "",
      phone: "",
      linkedin: "",
      video_url: "",
      pdf_url: "",
      pdf_caption: "",
      overview: "",
    },
    {
      company: "SignalWorks",
      value_prop: "Research + policy convenings.",
      website: "https://example.com",
      logo: "",
      contact_email: "",
      phone: "",
      linkedin: "",
      video_url: "",
      pdf_url: "",
      pdf_caption: "",
      overview: "",
    },
  ],

  sponsorships: [
    {
      id: "sp-1",
      company: "Greentide",
      scope: "event",
      tier: "main",
      status: "pending",
      event_id: 2,
      table_id: null,
      name: "Jamie",
      email: "jamie@greentide.example",
      value_prop: "Deployment playbooks.",
      message: "We'd like to anchor this room.",
    },
    {
      id: "sp-2",
      company: "Anyon AI",
      scope: "table",
      tier: "table",
      status: "approved",
      event_id: 1,
      table_id: 101,
      name: "Ava",
      email: "ava@anyonai.example",
      sponsor_table_name: "Anyon AI · Inference Economics",
      sponsor_moderator_name: "Eliza Marston",
      sponsor_moderator_linkedin: "",
      sponsor_moderator_avatar: "",
      sponsor_moderator_bio: "",
      cart_id: "",
      value_prop: "",
      message: "",
      logo: "",
      welcome_video_url: "https://www.youtube.com/watch?v=dQw4w9WgXcQ",
    },
  ],

  sponsorRegistry: [
    {
      id: "reg-1",
      company: "Greentide",
      contact_name: "Jamie",
      email: "jamie@greentide.example",
      interests: ["Climate", "Capital"],
      tiers_interested: ["main", "table"],
      value_prop: "Deployment playbooks.",
      status: "active",
      created_at: nowIso(),
      notifications_sent: 0,
      last_notified_at: null,
      paused_until: null,
    },
  ],

  vouchers: [
    {
      id: "v-1",
      code: "GREEN-Q2",
      event_id: 2,
      sponsor_company: "Greentide",
      max_uses: 25,
      used_count: 3,
      label: "Q2 guests",
      expires_at: "",
      attendees: [
        { name: "Alex", email: "alex@example.com", company: "—" },
        { name: "Priya", email: "priya@example.com", company: "—" },
        { name: "Sam", email: "sam@example.com", company: "—" },
      ],
    },
  ],

  inquiries: {
    sponsor: [],
    host: [
      {
        id: "h-1",
        created_at: nowIso(),
        name: "Taylor",
        email: "taylor@example.com",
        status: "pending",
      },
    ],
    moderator: [
      {
        id: "m-1",
        created_at: nowIso(),
        name: "Morgan",
        email: "morgan@example.com",
        expertise: "AI infra",
        role: "moderator",
        bio: "Built eval harnesses.",
        link: "https://example.com",
        status: "pending",
      },
    ],
    membership: [],
    general_waitlist: [{ created_at: nowIso(), email: "someone@example.com" }],
  },

  experiments: {
    trending_ab: {
      shipped: null,
      variants: {
        control: {
          impressions: 1200,
          clicks: 28,
          unique_clickers: 24,
          ctr_pct: 2.3,
        },
        personalized: {
          impressions: 1185,
          clicks: 39,
          unique_clickers: 33,
          ctr_pct: 3.3,
        },
      },
      lift_pct: 43,
    },
  },
};

const nextNumericId = (arr) => {
  const nums = arr.map((x) => Number(x.id)).filter((n) => Number.isFinite(n));
  return (nums.length ? Math.max(...nums) : 0) + 1;
};

const randomCode = () => Math.random().toString(36).slice(2, 10).toUpperCase();

const http = {
  get: async (url) => {
    if (url === "/events") return { data: deepClone(STATIC_DB.events) };
    if (url === "/tables") return { data: deepClone(STATIC_DB.tables) };
    if (url === "/trusted-brands") return { data: deepClone(STATIC_DB.brands) };
    if (url === "/site-content")
      return { data: deepClone(STATIC_DB.siteContent) };

    if (url === "/admin/inquiries")
      return { data: deepClone(STATIC_DB.inquiries) };
    if (url === "/admin/sponsorships")
      return { data: deepClone(STATIC_DB.sponsorships) };
    if (url === "/admin/sponsor-registry")
      return { data: deepClone(STATIC_DB.sponsorRegistry) };
    if (url === "/admin/vouchers")
      return { data: deepClone(STATIC_DB.vouchers) };
    if (url === "/admin/analytics/trending-ab")
      return { data: deepClone(STATIC_DB.experiments.trending_ab) };

    const sm = url.match(/^\/events\/(\d+)\/sessions$/);
    if (sm) {
      const eventId = Number(sm[1]);
      return {
        data: deepClone(
          STATIC_DB.sessions.filter((s) => Number(s.event_id) === eventId),
        ),
      };
    }

    return { data: null };
  },

  post: async (url, body = {}) => {
    if (url === "/admin/events") {
      const id = nextNumericId(STATIC_DB.events);
      const ev = { ...body, id };
      STATIC_DB.events.push(ev);
      return { data: deepClone(ev) };
    }

    if (url === "/admin/sessions") {
      const id = nextNumericId(STATIC_DB.sessions);
      const s = { ...body, id };
      STATIC_DB.sessions.push(s);
      return { data: deepClone(s) };
    }

    if (url === "/admin/tables") {
      const id = nextNumericId(STATIC_DB.tables);
      const t = { ...body, id };
      STATIC_DB.tables.push(t);
      return { data: deepClone(t) };
    }

    const sd = url.match(/^\/admin\/sponsorships\/([^/]+)\/decision$/);
    if (sd) {
      const id = sd[1];
      const decision = body.decision;
      const s = STATIC_DB.sponsorships.find((x) => String(x.id) === String(id));
      if (s) s.status = decision === "approve" ? "approved" : "declined";
      return { data: { ok: true } };
    }

    const ha = url.match(/^\/admin\/host-inquiries\/([^/]+)\/approve$/);
    if (ha) {
      const id = ha[1];
      const row = (STATIC_DB.inquiries.host || []).find(
        (x) => String(x.id) === String(id),
      );
      if (row) row.status = "approved";
      return { data: { email: row?.email || "" } };
    }

    const ma = url.match(/^\/admin\/moderator-applications\/([^/]+)\/approve$/);
    if (ma) {
      const id = ma[1];
      const row = (STATIC_DB.inquiries.moderator || []).find(
        (x) => String(x.id) === String(id),
      );
      if (row) {
        row.status = "approved";
        row.public_title = body.title || row.expertise || "";
        row.avatar = body.avatar || "";
      }
      return { data: { ok: true } };
    }
    const md = url.match(/^\/admin\/moderator-applications\/([^/]+)\/decline$/);
    if (md) {
      const id = md[1];
      const row = (STATIC_DB.inquiries.moderator || []).find(
        (x) => String(x.id) === String(id),
      );
      if (row) row.status = "declined";
      return { data: { ok: true } };
    }

    const kick = url.match(/^\/tables\/(\d+)\/kick$/);
    if (kick) {
      const id = Number(kick[1]);
      const t = STATIC_DB.tables.find((x) => Number(x.id) === id);
      if (t) {
        const email = (body.user_email || "").toLowerCase();
        const name = (body.user_name || "").toLowerCase();
        t.attendees = (t.attendees || []).filter((a) => {
          const ae = (a.email || "").toLowerCase();
          const an = (a.name || "").toLowerCase();
          if (email && ae) return ae !== email;
          if (name && an) return an !== name;
          return true;
        });
        t.taken = Math.max(0, Number(t.taken || 0) - 1);
      }
      return { data: { ok: true } };
    }

    if (url === "/admin/upload-image") {
      const content_type = body.content_type || "image/png";
      const base64 = body.data_base64 || "";
      return { data: { url: `data:${content_type};base64,${base64}` } };
    }

    if (url === "/admin/test-email") {
      return {
        data: {
          delivered: false,
          to: body.to,
          provider: "outbox",
          outbox_id: randomCode(),
        },
      };
    }

    if (url === "/admin/sponsor-registry/sweep") {
      const summary = {
        notifications_sent: Math.min(
          3,
          (STATIC_DB.sponsorRegistry || []).filter(
            (r) => (r.status || "active") === "active",
          ).length,
        ),
        events_with_open_slots: 2,
        ran_at: nowIso(),
      };
      (STATIC_DB.sponsorRegistry || []).forEach((r) => {
        if ((r.status || "active") === "active") {
          r.notifications_sent = Number(r.notifications_sent || 0) + 1;
          r.last_notified_at = nowIso();
        }
      });
      return { data: summary };
    }

    if (url === "/admin/vouchers") {
      const id = `v-${randomCode()}`;
      const code = randomCode();
      const v = {
        id,
        code,
        event_id: body.event_id,
        sponsor_company: body.sponsor_company,
        max_uses: Number(body.max_uses || 25),
        used_count: 0,
        label: body.label || "",
        expires_at: body.expires_at || "",
        attendees: [],
      };
      STATIC_DB.vouchers.unshift(v);
      return { data: deepClone(v) };
    }

    if (url === "/admin/experiments/trending/ship") {
      STATIC_DB.experiments.trending_ab.shipped = body.variant || null;
      return { data: { ok: true } };
    }

    if (url === "/moderator-apply") {
      const id = `m-${randomCode()}`;
      STATIC_DB.inquiries.moderator.unshift({
        id,
        created_at: nowIso(),
        status: "pending",
        ...body,
      });
      return { data: { ok: true } };
    }

    return { data: { ok: true } };
  },

  put: async (url, body = {}) => {
    const eu = url.match(/^\/admin\/events\/(\d+)$/);
    if (eu) {
      const id = Number(eu[1]);
      const idx = STATIC_DB.events.findIndex((x) => Number(x.id) === id);
      if (idx >= 0)
        STATIC_DB.events[idx] = { ...STATIC_DB.events[idx], ...body, id };
      return { data: deepClone(STATIC_DB.events[idx]) };
    }

    const su = url.match(/^\/admin\/sessions\/(\d+)$/);
    if (su) {
      const id = Number(su[1]);
      const idx = STATIC_DB.sessions.findIndex((x) => Number(x.id) === id);
      if (idx >= 0)
        STATIC_DB.sessions[idx] = { ...STATIC_DB.sessions[idx], ...body, id };
      return { data: deepClone(STATIC_DB.sessions[idx]) };
    }

    const tu = url.match(/^\/admin\/tables\/(\d+)$/);
    if (tu) {
      const id = Number(tu[1]);
      const idx = STATIC_DB.tables.findIndex((x) => Number(x.id) === id);
      if (idx >= 0)
        STATIC_DB.tables[idx] = { ...STATIC_DB.tables[idx], ...body, id };
      return { data: deepClone(STATIC_DB.tables[idx]) };
    }

    const bu = url.match(/^\/admin\/brands\/(.+)$/);
    if (bu) {
      const company = decodeURIComponent(bu[1]);
      const idx = STATIC_DB.brands.findIndex(
        (x) => (x.company || "").toLowerCase() === company.toLowerCase(),
      );
      if (idx >= 0)
        STATIC_DB.brands[idx] = {
          ...STATIC_DB.brands[idx],
          ...body,
          company: STATIC_DB.brands[idx].company,
        };
      return { data: deepClone(STATIC_DB.brands[idx]) };
    }

    if (url === "/admin/site-content") {
      STATIC_DB.siteContent = { ...STATIC_DB.siteContent, ...body };
      return { data: deepClone(STATIC_DB.siteContent) };
    }

    return { data: { ok: true } };
  },

  delete: async (url) => {
    const ed = url.match(/^\/admin\/events\/(\d+)$/);
    if (ed) {
      const id = Number(ed[1]);
      STATIC_DB.events = STATIC_DB.events.filter((x) => Number(x.id) !== id);
      STATIC_DB.sessions = STATIC_DB.sessions.filter(
        (s) => Number(s.event_id) !== id,
      );
      STATIC_DB.tables = STATIC_DB.tables.filter(
        (t) => Number(t.event_id) !== id,
      );
      return { data: { ok: true } };
    }

    const sd = url.match(/^\/admin\/sessions\/(\d+)$/);
    if (sd) {
      const id = Number(sd[1]);
      STATIC_DB.sessions = STATIC_DB.sessions.filter(
        (x) => Number(x.id) !== id,
      );
      return { data: { ok: true } };
    }

    const td = url.match(/^\/admin\/tables\/(\d+)$/);
    if (td) {
      const id = Number(td[1]);
      STATIC_DB.tables = STATIC_DB.tables.filter((x) => Number(x.id) !== id);
      return { data: { ok: true } };
    }

    const rd = url.match(/^\/admin\/sponsor-registry\/([^/]+)$/);
    if (rd) {
      const id = rd[1];
      STATIC_DB.sponsorRegistry = STATIC_DB.sponsorRegistry.filter(
        (x) => String(x.id) !== String(id),
      );
      return { data: { ok: true } };
    }

    const vd = url.match(/^\/admin\/vouchers\/([^/]+)$/);
    if (vd) {
      const id = vd[1];
      STATIC_DB.vouchers = STATIC_DB.vouchers.filter(
        (x) => String(x.id) !== String(id),
      );
      return { data: { ok: true } };
    }

    return { data: { ok: true } };
  },
};

const formatApiErrorDetail = (err) => {
  try {
    if (!err) return "";
    if (typeof err === "string") return err;
    if (err?.response?.data?.detail) return String(err.response.data.detail);
    if (err?.message) return String(err.message);
    return "";
  } catch {
    return "";
  }
};

/* ----------------------------
   PAGE
----------------------------- */
const EMPTY_EVENT = {
  title: "",
  description: "",
  start_time: "",
  end_time: "",
  timezone: "UTC",
  cover: "",
  host: "",
  registered: 0,
  tables_count: 0,
  tags: [],
};
const EMPTY_TABLE = {
  event_id: "",
  topic: "",
  description: "",
  moderator: "",
  moderator_avatar: "",
  seats: 8,
  taken: 0,
  circle: "",
  is_live: false,
  attendees: [],
};
const EMPTY_SESSION = {
  event_id: "",
  title: "",
  description: "",
  start_time: "",
  end_time: "",
  speaker: "",
};

export default function Admin() {
  const { user } = useAuth();
  const [tab, setTab] = useState("events");
  const [events, setEvents] = useState([]);
  const [tables, setTables] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [brands, setBrands] = useState([]);
  const [sponsorships, setSponsorships] = useState([]);
  const [inquiries, setInquiries] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [vouchers, setVouchers] = useState([]);

  const reload = async () => {
    const [e, t, b, sp, reg, vch] = await Promise.all([
      http.get("/events").then((r) => r.data),
      http.get("/tables").then((r) => r.data),
      http.get("/trusted-brands").then((r) => r.data),
      http
        .get("/admin/sponsorships")
        .then((r) => r.data)
        .catch(() => []),
      http
        .get("/admin/sponsor-registry")
        .then((r) => r.data)
        .catch(() => []),
      http
        .get("/admin/vouchers")
        .then((r) => r.data)
        .catch(() => []),
    ]);
    setEvents(e);
    setTables(t);
    setBrands(b);
    setSponsorships(sp);
    setRegistry(reg);
    setVouchers(vch);
    const all = await Promise.all(
      e.map((ev) => http.get(`/events/${ev.id}/sessions`).then((r) => r.data)),
    );
    setSessions(all.flat());
  };

  useEffect(() => {
    if (user && typeof user === "object" && user.role === "admin") {
      reload();
      http
        .get("/admin/inquiries")
        .then(({ data }) => setInquiries(data))
        .catch(() => {});
      http
        .get("/site-content")
        .then(({ data }) => setSiteContent(data))
        .catch(() => {});
    }
  }, [user]);

  if (user === null)
    return <div className="container-x adminLoading">Loading…</div>;
  if (user === false || (user && user.role !== "admin")) {
    return (
      <div
        className="container-x adminLocked"
        data-testid="admin-locked"
      >
        <Lock size={22} className="adminLockedIcon" />
        <p className="overline adminLockedOverline">Admin panel</p>
        <h1 className="adminLockedHeading">
          Sign in as admin.
        </h1>
        <p className="adminLockedBody">
          Use the seeded admin credentials in{" "}
          <code className="adminLockedCode">admin@vmeetio.io</code> to access
          this panel.
        </p>
        <Link to="/login" className="btn-gold">
          Go to sign in
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="admin-page" className="adminPage">
      <section
        style={{
          background: "linear-gradient(180deg, #061412 0%, #0A1D1A 100%)",
        }}
        className="adminHeader"
      >
        <div className="container-x adminHeaderInner">
          <Link
            to="/"
            className="adminBackLink"
          >
            <ArrowLeft size={12} /> Back to site
          </Link>
          <div className="adminTitleRow">
            <h1 className="adminTitle">
              Admin panel
            </h1>
            <div className="adminTitleRight">
              <Link
                to="/admin/onboarding"
                data-testid="admin-onboarding-link"
                className="adminOnboardingLink"
              >
                <Sparkles size={12} /> First-run wizard
              </Link>
              <p className="overline">Signed in as {user.email}</p>
            </div>
          </div>
          <div
            className="adminTabs"
            data-testid="admin-tabs"
          >
            {[
              ["events", "Events"],
              ["sessions", "Master sessions"],
              ["tables", "Tables"],
              ["brands", "Brands"],
              ["sponsorships", "Sponsorships"],
              ["inquiries", "Inquiries"],
              ["moderators", "Moderator queue"],
              ["content", "Site content"],
              ["registry", "Sponsor registry"],
              ["vouchers", "Vouchers"],
              ["experiments", "Experiments"],
            ].map(([k, l]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                data-testid={`admin-tab-${k}`}
                className={`adminTabButton ${
                  tab === k ? "adminTabButtonActive" : ""
                }`}
              >
                {l}
                {tab === k && (
                  <span className="adminTabIndicator" />
                )}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="section">
        <div className="container-x">
          {tab === "events" && <EventsTab events={events} reload={reload} />}
          {tab === "sessions" && (
            <SessionsTab events={events} sessions={sessions} reload={reload} />
          )}
          {tab === "tables" && (
            <TablesTab events={events} tables={tables} reload={reload} />
          )}
          {tab === "brands" && <BrandsTab brands={brands} reload={reload} />}
          {tab === "sponsorships" && (
            <SponsorshipsTab
              sponsorships={sponsorships}
              events={events}
              tables={tables}
              reload={reload}
            />
          )}
          {tab === "inquiries" && (
            <InquiriesTab inquiries={inquiries} reload={reload} />
          )}
          {tab === "moderators" && (
            <ModeratorQueueTab inquiries={inquiries} reload={reload} />
          )}
          {tab === "content" && (
            <SiteContentTab
              siteContent={siteContent}
              setSiteContent={setSiteContent}
            />
          )}
          {tab === "registry" && (
            <SponsorRegistryTab registry={registry} reload={reload} />
          )}
          {tab === "vouchers" && (
            <VouchersTab vouchers={vouchers} events={events} reload={reload} />
          )}
          {tab === "experiments" && <ExperimentsTab />}
        </div>
      </section>
    </div>
  );
}

function Field({ label, value, onChange, type = "text", textarea, ...rest }) {
  const Comp = textarea ? "textarea" : "input";
  return (
    <label className="fieldBlock">
      <span className="overline fieldLabel">{label}</span>
      <Comp
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        className="fieldInput"
        {...rest}
      />
    </label>
  );
}

function EventsTab({ events, reload }) {
  const [draft, setDraft] = useState(EMPTY_EVENT);
  const save = async (ev) => {
    await http.put(`/admin/events/${ev.id}`, ev);
    toast.success(`Saved "${ev.title}".`);
    reload();
  };
  const createNew = async () => {
    if (!draft.title) return toast.error("Title required.");
    await http.post("/admin/events", draft);
    toast.success("Event created.");
    setDraft(EMPTY_EVENT);
    reload();
  };
  const del = async (id) => {
    if (!confirm("Delete this event and all its sessions/tables?")) return;
    await http.delete(`/admin/events/${id}`);
    toast.success("Deleted.");
    reload();
  };

  return (
    <div className="tabSpace6" data-testid="admin-events-tab">
      <div className="glass cardPanel">
        <p className="overline cardOverline">
          <Plus size={12} /> Add a new event
        </p>
        <div className="gridTwoCol">
          <Field
            label="Title"
            value={draft.title}
            onChange={(v) => setDraft({ ...draft, title: v })}
          />
          <Field
            label="Host"
            value={draft.host}
            onChange={(v) => setDraft({ ...draft, host: v })}
          />
          <Field
            label="Start time (ISO)"
            value={draft.start_time}
            onChange={(v) => setDraft({ ...draft, start_time: v })}
            placeholder="2026-05-01T18:00:00Z"
          />
          <Field
            label="End time (ISO)"
            value={draft.end_time}
            onChange={(v) => setDraft({ ...draft, end_time: v })}
          />
          <Field
            label="Timezone"
            value={draft.timezone}
            onChange={(v) => setDraft({ ...draft, timezone: v })}
            placeholder="America/New_York"
          />
          <Field
            label="Cover image URL"
            value={draft.cover}
            onChange={(v) => setDraft({ ...draft, cover: v })}
          />
          <Field
            label="Registered"
            type="number"
            value={draft.registered}
            onChange={(v) => setDraft({ ...draft, registered: Number(v) })}
          />
          <Field
            label="Tables count"
            type="number"
            value={draft.tables_count}
            onChange={(v) => setDraft({ ...draft, tables_count: Number(v) })}
          />
        </div>
        <div className="mt4">
          <Field
            label="Description"
            textarea
            rows={3}
            value={draft.description}
            onChange={(v) => setDraft({ ...draft, description: v })}
          />
        </div>
        <button
          onClick={createNew}
          className="btn-gold mt5"
          data-testid="admin-create-event"
        >
          Create event
        </button>
      </div>

      {events.map((ev) => (
        <details
          key={ev.id}
          className="glass cardPanel"
          data-testid={`admin-event-${ev.id}`}
        >
          <summary className="eventSummary">
            <span>{ev.title}</span>
            <span className="overline">{ev.id}</span>
          </summary>
          <div className="gridTwoCol mt5">
            <Field
              label="Title"
              value={ev.title}
              onChange={(v) => (ev.title = v)}
            />
            <Field
              label="Host"
              value={ev.host}
              onChange={(v) => (ev.host = v)}
            />
            <Field
              label="Start time (ISO)"
              value={ev.start_time}
              onChange={(v) => (ev.start_time = v)}
            />
            <Field
              label="End time (ISO)"
              value={ev.end_time}
              onChange={(v) => (ev.end_time = v)}
            />
            <Field
              label="Timezone"
              value={ev.timezone}
              onChange={(v) => (ev.timezone = v)}
            />
            <Field
              label="Cover image URL"
              value={ev.cover}
              onChange={(v) => (ev.cover = v)}
            />
            <Field
              label="Registered"
              type="number"
              value={ev.registered}
              onChange={(v) => (ev.registered = Number(v))}
            />
            <Field
              label="Tables count"
              type="number"
              value={ev.tables_count}
              onChange={(v) => (ev.tables_count = Number(v))}
            />
          </div>
          <div className="mt4">
            <Field
              label="Description"
              textarea
              rows={3}
              value={ev.description}
              onChange={(v) => (ev.description = v)}
            />
          </div>
          <div className="mt4">
            <Field
              label="Gallery images — up to 3 URLs, one per line"
              textarea
              rows={3}
              value={(ev.images || []).join("\n")}
              onChange={(v) =>
                (ev.images = v
                  .split("\n")
                  .map((s) => s.trim())
                  .filter(Boolean)
                  .slice(0, 3))
              }
            />
            {(ev.images || []).length > 0 && (
              <div className="eventGallery">
                {(ev.images || []).slice(0, 3).map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    className="eventGalleryImg"
                  />
                ))}
              </div>
            )}
          </div>
          <div className="mt4">
            <label
              className="sponsorshipCheckLabel"
              data-testid={`admin-event-open-sponsorship-${ev.id}`}
            >
              <input
                type="checkbox"
                defaultChecked={ev.is_open_for_sponsorship !== false}
                onChange={(e) => {
                  ev.is_open_for_sponsorship = e.target.checked;
                }}
                className="sponsorshipCheckbox"
                data-testid={`admin-event-open-sponsorship-input-${ev.id}`}
              />
              <span>
                <strong className="sponsorshipCheckTitle">
                  Open for sponsorship cart
                </strong>
                <span className="sponsorshipCheckHint">
                  When off, this event is hidden from the sponsor multi-table
                  cart and new Main/Table sponsor purchases are blocked.
                </span>
              </span>
            </label>
          </div>
          <div className="mt5 btnRow">
            <button
              onClick={() => save(ev)}
              className="btn-gold btnGoldSm"
              data-testid={`admin-save-event-${ev.id}`}
            >
              <Save size={14} /> Save
            </button>
            <button
              onClick={() => del(ev.id)}
              className="btn-ghost btnGhostXsRed"
              data-testid={`admin-delete-event-${ev.id}`}
            >
              <Trash2 size={14} /> Delete
            </button>
          </div>
        </details>
      ))}
    </div>
  );
}

function SessionsTab({ events, sessions, reload }) {
  const [draft, setDraft] = useState(EMPTY_SESSION);

  const create = async () => {
    if (!draft.event_id || !draft.title)
      return toast.error("Event + title required.");
    await http.post("/admin/sessions", draft);
    toast.success("Session created.");
    setDraft(EMPTY_SESSION);
    reload();
  };
  const save = async (s) => {
    await http.put(`/admin/sessions/${s.id}`, s);
    toast.success("Saved.");
    reload();
  };
  const del = async (id) => {
    await http.delete(`/admin/sessions/${id}`);
    toast.success("Deleted.");
    reload();
  };

  return (
    <div className="tabSpace6" data-testid="admin-sessions-tab">
      <div className="glass cardPanel">
        <p className="overline cardOverline">
          <Plus size={12} /> Add a master session
        </p>
        <div className="gridTwoCol">
          <label className="fieldBlock">
            <span className="overline fieldLabel">Event</span>
            <select
              value={draft.event_id}
              onChange={(e) => setDraft({ ...draft, event_id: e.target.value })}
              className="selectInput"
            >
              <option value="" className="selectOption">
                Pick event
              </option>
              {events.map((e) => (
                <option key={e.id} value={e.id} className="selectOption">
                  {e.title}
                </option>
              ))}
            </select>
          </label>
          <Field
            label="Title"
            value={draft.title}
            onChange={(v) => setDraft({ ...draft, title: v })}
          />
          <Field
            label="Start time (ISO)"
            value={draft.start_time}
            onChange={(v) => setDraft({ ...draft, start_time: v })}
          />
          <Field
            label="End time (ISO)"
            value={draft.end_time}
            onChange={(v) => setDraft({ ...draft, end_time: v })}
          />
          <Field
            label="Speaker"
            value={draft.speaker}
            onChange={(v) => setDraft({ ...draft, speaker: v })}
          />
        </div>
        <div className="mt4">
          <Field
            label="Description"
            textarea
            rows={2}
            value={draft.description}
            onChange={(v) => setDraft({ ...draft, description: v })}
          />
        </div>
        <button onClick={create} className="btn-gold mt5">
          Create session
        </button>
      </div>

      <div className="tabSpace3">
        {sessions.map((s) => (
          <details key={s.id} className="glass cardPanelSmall">
            <summary className="sessionSummary">
              <span>
                <span className="overline sessionOverline">{s.event_id}</span>
                {s.title}
              </span>
              <span className="sessionTime">{s.start_time}</span>
            </summary>
            <div className="gridTwoColGap3 mt4">
              <Field
                label="Title"
                value={s.title}
                onChange={(v) => (s.title = v)}
              />
              <Field
                label="Speaker"
                value={s.speaker}
                onChange={(v) => (s.speaker = v)}
              />
              <Field
                label="Start (ISO)"
                value={s.start_time}
                onChange={(v) => (s.start_time = v)}
              />
              <Field
                label="End (ISO)"
                value={s.end_time}
                onChange={(v) => (s.end_time = v)}
              />
            </div>
            <div className="mt3">
              <Field
                label="Description"
                textarea
                rows={2}
                value={s.description}
                onChange={(v) => (s.description = v)}
              />
            </div>
            <div className="mt3 btnRowSmall">
              <button onClick={() => save(s)} className="btn-gold btnGoldXs">
                <Save size={12} /> Save
              </button>
              <button
                onClick={() => del(s.id)}
                className="btn-ghost btnGhostXsRed"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function TablesTab({ events, tables, reload }) {
  const [draft, setDraft] = useState(EMPTY_TABLE);

  const create = async () => {
    if (!draft.event_id || !draft.topic)
      return toast.error("Event + topic required.");
    await http.post("/admin/tables", draft);
    toast.success("Table created.");
    setDraft(EMPTY_TABLE);
    reload();
  };
  const save = async (t) => {
    await http.put(`/admin/tables/${t.id}`, t);
    toast.success("Saved.");
    reload();
  };
  const del = async (id) => {
    await http.delete(`/admin/tables/${id}`);
    toast.success("Deleted.");
    reload();
  };

  return (
    <div className="tabSpace6" data-testid="admin-tables-tab">
      <div className="glass cardPanel">
        <p className="overline cardOverline">
          <Plus size={12} /> Add a table
        </p>
        <div className="gridTwoCol">
          <label className="fieldBlock">
            <span className="overline fieldLabel">Event</span>
            <select
              value={draft.event_id}
              onChange={(e) => setDraft({ ...draft, event_id: e.target.value })}
              className="selectInput"
            >
              <option value="" className="selectOption">
                Pick event
              </option>
              {events.map((e) => (
                <option key={e.id} value={e.id} className="selectOption">
                  {e.title}
                </option>
              ))}
            </select>
          </label>
          <Field
            label="Topic"
            value={draft.topic}
            onChange={(v) => setDraft({ ...draft, topic: v })}
          />
          <Field
            label="Moderator"
            value={draft.moderator}
            onChange={(v) => setDraft({ ...draft, moderator: v })}
          />
          <Field
            label="Moderator avatar URL"
            value={draft.moderator_avatar}
            onChange={(v) => setDraft({ ...draft, moderator_avatar: v })}
          />
          <Field
            label="Circle"
            value={draft.circle}
            onChange={(v) => setDraft({ ...draft, circle: v })}
          />
          <Field
            label="Seats"
            type="number"
            value={draft.seats}
            onChange={(v) => setDraft({ ...draft, seats: Number(v) })}
          />
        </div>
        <div className="mt4">
          <Field
            label="Description"
            textarea
            rows={2}
            value={draft.description}
            onChange={(v) => setDraft({ ...draft, description: v })}
          />
        </div>
        <button onClick={create} className="btn-gold mt5">
          Create table
        </button>
      </div>

      <div className="tabSpace3">
        {tables.map((t) => (
          <details key={t.id} className="glass cardPanelSmall">
            <summary className="tableSummary">
              <span>
                <span className="overline sessionOverline">{t.event_id}</span>
                {t.topic}
              </span>
              <span className="tableSeats">
                {t.taken}/{t.seats}
              </span>
            </summary>
            <div className="gridTwoColGap3 mt4">
              <Field
                label="Topic"
                value={t.topic}
                onChange={(v) => (t.topic = v)}
              />
              <Field
                label="Moderator"
                value={t.moderator}
                onChange={(v) => (t.moderator = v)}
              />
              <Field
                label="Moderator avatar URL"
                value={t.moderator_avatar}
                onChange={(v) => (t.moderator_avatar = v)}
              />
              <Field
                label="Circle"
                value={t.circle}
                onChange={(v) => (t.circle = v)}
              />
              <Field
                label="Seats"
                type="number"
                value={t.seats}
                onChange={(v) => (t.seats = Number(v))}
              />
              <Field
                label="Taken"
                type="number"
                value={t.taken}
                onChange={(v) => (t.taken = Number(v))}
              />
            </div>
            <div className="mt3">
              <Field
                label="Description"
                textarea
                rows={2}
                value={t.description}
                onChange={(v) => (t.description = v)}
              />
            </div>
            <div className="mt3 btnRowSmall">
              <button onClick={() => save(t)} className="btn-gold btnGoldXs">
                <Save size={12} /> Save
              </button>
              <button
                onClick={() => del(t.id)}
                className="btn-ghost btnGhostXsRed"
              >
                <Trash2 size={12} /> Delete
              </button>
            </div>

            <div
              className="tableAttendeesSection"
              data-testid={`admin-table-attendees-${t.id}`}
            >
              <p className="overline tableAttendeesOverline">
                Seated · {t.attendees?.length || 0}/{t.seats}
              </p>
              {!t.attendees || t.attendees.length === 0 ? (
                <p className="tableAttendeesEmpty">No-one seated yet.</p>
              ) : (
                <ul className="tableAttendeesList">
                  {t.attendees.map((a, i) => (
                    <li
                      key={i}
                      className="tableAttendeeRow"
                    >
                      <span className="tableAttendeeName">
                        {a.name}
                        {a.email ? (
                          <span className="tableAttendeeEmail"> · {a.email}</span>
                        ) : null}
                      </span>
                      <button
                        onClick={async () => {
                          const reason = window.prompt(
                            `Remove ${a.name} from "${t.topic}"?\n\nOptional reason (sent to them):`,
                            "Removed by event organizer for room decorum.",
                          );
                          if (reason === null) return;
                          try {
                            await http.post(`/tables/${t.id}/kick`, {
                              user_email: a.email || "",
                              user_id: a.user_id || a.id || "",
                              user_name: a.name || "",
                              reason,
                            });
                            toast.success(`${a.name} removed.`);
                            reload();
                          } catch (err) {
                            toast.error(
                              err?.response?.data?.detail ||
                                "Could not remove.",
                            );
                          }
                        }}
                        data-testid={`admin-table-kick-${t.id}-${i}`}
                        className="tableAttendeeKick"
                      >
                        Remove
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </details>
        ))}
      </div>
    </div>
  );
}

function BrandsTab({ brands, reload }) {
  const save = async (b) => {
    await http.put(`/admin/brands/${encodeURIComponent(b.company)}`, b);
    toast.success("Saved.");
    reload();
  };
  return (
    <div className="tabSpace3" data-testid="admin-brands-tab">
      {brands.map((b) => (
        <details key={b.company} className="glass cardPanelSmall">
          <summary className="brandSummary">
            {b.company}
          </summary>
          <div className="gridTwoColGap3 mt4">
            <Field
              label="Value prop"
              value={b.value_prop}
              onChange={(v) => (b.value_prop = v)}
            />
            <Field
              label="Website"
              value={b.website}
              onChange={(v) => (b.website = v)}
            />
            <Field
              label="Logo URL"
              value={b.logo}
              onChange={(v) => (b.logo = v)}
            />
            <Field
              label="Contact email"
              value={b.contact_email}
              onChange={(v) => (b.contact_email = v)}
            />
            <Field
              label="Phone"
              value={b.phone}
              onChange={(v) => (b.phone = v)}
            />
            <Field
              label="LinkedIn URL"
              value={b.linkedin}
              onChange={(v) => (b.linkedin = v)}
            />
            <Field
              label="Video URL (embed)"
              value={b.video_url}
              onChange={(v) => (b.video_url = v)}
            />
            <Field
              label="PDF / one-pager URL"
              value={b.pdf_url}
              onChange={(v) => (b.pdf_url = v)}
            />
            <Field
              label="PDF caption (optional)"
              value={b.pdf_caption}
              onChange={(v) => (b.pdf_caption = v)}
            />
          </div>
          <div className="mt3">
            <Field
              label="Overview"
              textarea
              rows={3}
              value={b.overview}
              onChange={(v) => (b.overview = v)}
            />
          </div>
          <div className="mt3">
            <button onClick={() => save(b)} className="btn-gold btnGoldXs">
              <Save size={12} /> Save
            </button>
          </div>
        </details>
      ))}
    </div>
  );
}

function SponsorshipsTab({ sponsorships, events, tables, reload }) {
  const decide = async (id, decision) => {
    try {
      await http.post(`/admin/sponsorships/${id}/decision`, { decision });
      toast.success(`Sponsorship ${decision}d.`);
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed.");
    }
  };

  const buckets = {
    pending: sponsorships.filter((s) => (s.status || "pending") === "pending"),
    approved: sponsorships.filter((s) => s.status === "approved"),
    declined: sponsorships.filter((s) => s.status === "declined"),
  };

  const renderRow = (s) => {
    const event = events.find((e) => e.id === s.event_id);
    const table = s.table_id ? tables.find((t) => t.id === s.table_id) : null;
    return (
      <div
        key={s.id}
        data-testid={`admin-sponsorship-${s.id}`}
        className="glass sponsorshipRow"
      >
        <div className="sponsorshipRowInner">
          <div className="sponsorshipLeft">
            <BrandLogo company={s.company} size={44} />
            <div className="sponsorshipInfo">
              <p className="sponsorshipCompany">{s.company}</p>
              <p className="overline sponsorshipScope">
                {s.scope} · {s.tier}
              </p>
              {event && (
                <p className="sponsorshipEvent">
                  Event: {event.title}
                </p>
              )}
              {table && (
                <p className="sponsorshipTable">Table: {table.topic}</p>
              )}
              {s.sponsor_table_name && (
                <p
                  className="sponsorshipTableName"
                  data-testid={`admin-sponsor-table-name-${s.id}`}
                >
                  Sponsor table name:{" "}
                  <strong className="sponsorshipTableNameValue">
                    {s.sponsor_table_name}
                  </strong>
                </p>
              )}
              {s.sponsor_moderator_name && (
                <p
                  className="sponsorshipModerator"
                  data-testid={`admin-sponsor-moderator-${s.id}`}
                >
                  Sponsor moderator:{" "}
                  <strong className="sponsorshipModeratorName">
                    {s.sponsor_moderator_name}
                  </strong>
                  {s.sponsor_moderator_linkedin && (
                    <>
                      {" "}
                      ·{" "}
                      <a
                        href={s.sponsor_moderator_linkedin}
                        target="_blank"
                        rel="noreferrer"
                        className="sponsorshipModeratorLink"
                      >
                        LinkedIn
                      </a>
                    </>
                  )}
                </p>
              )}
              {s.sponsor_moderator_bio && (
                <p className="sponsorshipModeratorBio">
                  "{s.sponsor_moderator_bio}"
                </p>
              )}
              {s.cart_id && (
                <p className="sponsorshipCartId">
                  Cart batch · {s.cart_id.slice(0, 8)}
                </p>
              )}
              <p className="sponsorshipContact">
                {s.name} · {s.email}
              </p>
              {s.value_prop && (
                <p className="sponsorshipValueProp">
                  "{s.value_prop}"
                </p>
              )}
              {s.message && (
                <p className="sponsorshipMessage">{s.message}</p>
              )}
            </div>
          </div>
          <div className="sponsorshipActions">
            {(s.status || "pending") === "pending" ? (
              <>
                <button
                  onClick={() => decide(s.id, "approve")}
                  data-testid={`admin-sponsor-approve-${s.id}`}
                  className="sponsorshipApproveBtn"
                >
                  Approve
                </button>
                <button
                  onClick={() => decide(s.id, "decline")}
                  data-testid={`admin-sponsor-decline-${s.id}`}
                  className="sponsorshipDeclineBtn"
                >
                  Decline
                </button>
              </>
            ) : s.status === "approved" ? (
              <span className="sponsorshipApprovedBadge">
                Approved
              </span>
            ) : (
              <span className="sponsorshipDeclinedBadge">
                Declined
              </span>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="tabSpace8" data-testid="admin-sponsorships-tab">
      <div>
        <p className="overline sponsorshipSectionLabel">Pending · {buckets.pending.length}</p>
        {buckets.pending.length === 0 ? (
          <p className="sponsorshipEmptyText">Nothing waiting.</p>
        ) : (
          buckets.pending.map(renderRow)
        )}
      </div>
      <div>
        <p className="overline sponsorshipSectionLabel">Approved · {buckets.approved.length}</p>
        {buckets.approved.map(renderRow)}
      </div>
      <div>
        <p className="overline sponsorshipSectionLabel">Declined · {buckets.declined.length}</p>
        {buckets.declined.map(renderRow)}
      </div>
    </div>
  );
}

function InquiriesTab({ inquiries, reload }) {
  const [optimistic, setOptimistic] = useState({});
  const [showAll, setShowAll] = useState({});
  if (!inquiries) return <p className="inquiriesLoadingText">Loading…</p>;
  const sortedHost = [...(inquiries.host || [])].sort((a, b) =>
    (b.created_at || "").localeCompare(a.created_at || ""),
  );
  const sections = [
    ["Sponsor inquiries", inquiries.sponsor || [], null],
    ["Host inquiries", sortedHost, "host"],
    ["Moderator applications", inquiries.moderator || [], null],
    ["Membership applications", inquiries.membership || [], null],
    ["General waitlist", inquiries.general_waitlist || [], null],
  ];
  const approveHost = async (id) => {
    try {
      const { data } = await http.post(`/admin/host-inquiries/${id}/approve`);
      toast.success(`Approved · magic link sent to ${data.email}`);
      setOptimistic((prev) => ({ ...prev, [id]: "approved" }));
      reload?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e) || "Approve failed");
    }
  };
  return (
    <div className="tabSpace6" data-testid="admin-inquiries-tab">
      {sections.map(([title, rows, kind]) => {
        const expanded = !!showAll[title];
        const shown = expanded ? rows : rows.slice(0, 30);
        return (
          <div key={title} className="glass cardPanel">
            <div className="inquiriesSectionHeader">
              <p className="overline">
                {title} · {rows.length}
              </p>
              {rows.length > 30 && (
                <button
                  onClick={() =>
                    setShowAll((prev) => ({ ...prev, [title]: !expanded }))
                  }
                  className="inquiriesToggle"
                  data-testid={`admin-inquiries-toggle-${title.replace(/\s+/g, "-").toLowerCase()}`}
                >
                  {expanded ? `Collapse` : `Show all ${rows.length}`}
                </button>
              )}
            </div>
            {rows.length === 0 ? (
              <p className="inquiriesEmptyText">None yet.</p>
            ) : (
              <ul className="inquiriesList">
                {shown.map((r, i) => {
                  const status = optimistic[r.id] || r.status;
                  return (
                    <li
                      key={r.id || i}
                      className="inquiryRow"
                    >
                      <span>
                        <code className="inquiryTimestamp">
                          {r.created_at}
                        </code>{" "}
                        · {r.name || r.company || r.email}{" "}
                        {r.email && (
                          <span className="inquiryEmail">— {r.email}</span>
                        )}
                        {kind === "host" && status === "approved" && (
                          <span className="inquiryApprovedBadge">
                            approved
                          </span>
                        )}
                      </span>
                      {kind === "host" && status !== "approved" && (
                        <button
                          onClick={() => approveHost(r.id)}
                          className="inquiryApproveBtn"
                          data-testid={`approve-host-${r.id}`}
                        >
                          Approve + send link
                        </button>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      })}
    </div>
  );
}

function ModeratorQueueTab({ inquiries, reload }) {
  const all = inquiries?.moderator || [];
  const pending = all.filter((m) => (m.status || "pending") === "pending");
  const reviewed = all.filter((m) => (m.status || "pending") !== "pending");
  return (
    <div data-testid="admin-moderator-queue">
      <p className="overline modQueuePendingLabel">Pending applications ({pending.length})</p>
      {pending.length === 0 && (
        <p className="modQueueEmptyText">
          No pending applications. New submissions from /moderators/apply land
          here.
        </p>
      )}
      <div className="tabSpace3">
        {pending.map((m) => (
          <ModeratorApplicationCard key={m.id} app={m} reload={reload} />
        ))}
      </div>
      {reviewed.length > 0 && (
        <>
          <p className="overline modQueueDecidedLabel">Decided ({reviewed.length})</p>
          <div className="modQueueDecidedList">
            {reviewed.map((m) => (
              <div
                key={m.id}
                className="glass modQueueDecidedCard"
              >
                <span className="modQueueDecidedName">
                  <strong className="modQueueDecidedNameBold">{m.name}</strong>
                  <span className="modQueueDecidedEmail"> · {m.email}</span>
                </span>
                <span
                  className={`modQueueStatusBadge ${
                    m.status === "approved"
                      ? "modQueueStatusApproved"
                      : "modQueueStatusDeclined"
                  }`}
                >
                  {m.status}
                </span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ModeratorApplicationCard({ app, reload }) {
  const [busy, setBusy] = useState(false);
  const [title, setTitle] = useState(app.expertise || "");
  const [avatar, setAvatar] = useState("");

  const decide = async (action) => {
    setBusy(true);
    try {
      if (action === "approve") {
        await http.post(`/admin/moderator-applications/${app.id}/approve`, {
          title,
          avatar,
        });
      } else {
        await http.post(`/admin/moderator-applications/${app.id}/decline`);
      }
      toast.success(
        action === "approve" ? "Approved — added to /moderators" : "Declined",
      );
      reload();
    } catch (err) {
      toast.error(formatApiErrorDetail(err) || "Failed to decide");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="glass modAppCard" data-testid={`mod-app-${app.id}`}>
      <div className="modAppTop">
        <div className="modAppInfo">
          <p className="modAppName">{app.name}</p>
          <p className="modAppEmail">
            {app.email}
            {app.role ? ` · ${app.role}` : ""}
          </p>
          <p className="modAppExpertise">
            <strong>Expertise:</strong> {app.expertise || "—"}
          </p>
          {app.bio && (
            <p className="modAppBio">
              {app.bio}
            </p>
          )}
          {app.link && (
            <a
              href={app.link}
              target="_blank"
              rel="noreferrer"
              className="modAppLink"
            >
              {app.link}
            </a>
          )}
        </div>
      </div>
      <div className="modAppFields">
        <Field
          label="Public title (optional)"
          value={title}
          onChange={setTitle}
        />
        <Field
          label="Avatar URL (optional)"
          value={avatar}
          onChange={setAvatar}
        />
      </div>
      <div className="modAppActions">
        <button
          onClick={() => decide("approve")}
          disabled={busy}
          data-testid={`mod-app-approve-${app.id}`}
          className="modAppApproveBtn"
        >
          Approve
        </button>
        <button
          onClick={() => decide("decline")}
          disabled={busy}
          data-testid={`mod-app-decline-${app.id}`}
          className="modAppDeclineBtn"
        >
          Decline
        </button>
      </div>
    </div>
  );
}

const TEXT_FIELDS = [
  ["hero_overline", "Hero overline"],
  ["hero_headline_1", "Hero headline (line 1)"],
  ["hero_headline_2", "Hero headline (line 2, italic gold)"],
  ["hero_body", "Hero body copy"],
  ["hero_quote_text", "Hero member quote"],
  [
    "hero_quote_attribution",
    "Hero quote attribution (e.g. Member · Founder, X)",
  ],
  ["method_headline_1", "Method headline 1"],
  ["method_headline_2", "Method headline 2"],
  ["method_headline_3", "Method headline 3"],
  ["method_body", "Method body copy"],
  ["brands_overline", "Brands overline"],
  ["brands_headline_1", "Brands headline 1"],
  ["brands_headline_2", "Brands headline 2 (italic gold)"],
  ["circles_headline_1", "Circles headline 1"],
  ["circles_headline_2", "Circles headline 2 (italic gold)"],
];

const LIST_SECTIONS = [
  {
    key: "value_strip",
    title: "Hero · 'What happens here' value strip",
    help: "Three short cards under the hero. Use a lucide icon name (Users, Sparkles, Mic2, Globe2, Shuffle, Calendar, MessageCircle, Crown, Clock, Radio, Smartphone).",
    fields: [
      { key: "icon", label: "Icon name", placeholder: "Users" },
      { key: "title", label: "Title", placeholder: "Small tables" },
      {
        key: "body",
        label: "Body",
        placeholder: "8 seats per table, never more",
        textarea: true,
      },
    ],
    blank: { icon: "Sparkles", title: "", body: "" },
  },
  {
    key: "how_it_works",
    title: "How it works · steps (3–6 rows)",
    help: "Steps under 'How it works'. Pick a lucide icon name; vmeetio recognises the standard set (see hint above).",
    fields: [
      { key: "icon", label: "Icon name", placeholder: "Globe2" },
      {
        key: "title",
        label: "Step title",
        placeholder: "Join from web or app",
      },
      {
        key: "body",
        label: "Step body",
        placeholder: "One link from your inbox…",
        textarea: true,
      },
    ],
    blank: { icon: "Globe2", title: "", body: "" },
  },
  {
    key: "faq",
    title: "FAQ · expandable items",
    help: "Each row becomes a collapsible FAQ accordion on landing.",
    fields: [
      { key: "q", label: "Question", placeholder: "Will I be on camera?" },
      {
        key: "a",
        label: "Answer",
        placeholder: "Yes — vmeetio works because faces matter…",
        textarea: true,
        rows: 3,
      },
    ],
    blank: { q: "", a: "" },
  },
  {
    key: "testimonials",
    title: "Rotating testimonials (top of section)",
    help: "Cycles every 7 seconds in the hero testimonial strip.",
    fields: [
      {
        key: "quote",
        label: "Quote",
        placeholder: "This is the only room I cancel client calls for.",
        textarea: true,
      },
      { key: "name", label: "Name", placeholder: "Sarah Khan" },
      {
        key: "role",
        label: "Role · Company",
        placeholder: "Founder · Anyon AI",
      },
    ],
    blank: { quote: "", name: "", role: "" },
  },
  {
    key: "sponsor_tiers",
    title: "Sponsorship packages · public picker",
    help: "Price is shown verbatim. Cadence is the renewal copy (e.g. 'per event', 'per year').",
    fields: [
      { key: "id", label: "ID (slug)", placeholder: "table" },
      { key: "name", label: "Tier name", placeholder: "Table sponsor" },
      {
        key: "price",
        label: "Price (number)",
        placeholder: "1500",
        type: "number",
      },
      { key: "currency", label: "Currency", placeholder: "USD" },
      { key: "cadence", label: "Cadence", placeholder: "per event" },
      {
        key: "tagline",
        label: "Short tagline",
        placeholder: "Anchor one curated table",
      },
      {
        key: "perks",
        label: "Perks (one per line)",
        placeholder: "Logo on the table card\nMid-table dossier credit",
        textarea: true,
        rows: 4,
        asLines: true,
      },
    ],
    blank: {
      id: "",
      name: "",
      price: 0,
      currency: "USD",
      cadence: "per event",
      tagline: "",
      perks: [],
    },
  },
];

const IMAGE_FIELDS = [
  ["hero_image", "Hero background image"],
  ["logo_url", "Site logo (optional)"],
  ["membership_image", "Membership CTA image (optional)"],
];

function ListEditor({ section, items, onChange }) {
  const rows = Array.isArray(items) ? items : [];
  const update = (i, key, v) => {
    const next = rows.slice();
    next[i] = { ...next[i], [key]: v };
    onChange(next);
  };
  const add = () => onChange([...rows, { ...section.blank }]);
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= rows.length) return;
    const next = rows.slice();
    [next[i], next[j]] = [next[j], next[i]];
    onChange(next);
  };

  return (
    <div
      className="glass listEditorPanel"
      data-testid={`cms-list-${section.key}`}
    >
      <div className="listEditorHeader">
        <p className="overline">{section.title}</p>
        <button
          type="button"
          onClick={add}
          data-testid={`cms-list-${section.key}-add`}
          className="listEditorAddBtn"
        >
          + Add row
        </button>
      </div>
      {section.help && (
        <p className="listEditorHelp">{section.help}</p>
      )}

      {rows.length === 0 && (
        <p className="listEditorEmpty">
          No rows yet — click "Add row" to start.
        </p>
      )}

      <div className="listEditorRows">
        {rows.map((row, i) => (
          <div
            key={i}
            data-testid={`cms-list-${section.key}-row-${i}`}
            className="listEditorRowCard"
          >
            <div className="listEditorRowHeader">
              <span className="listEditorRowNumber">Row {i + 1}</span>
              <div className="listEditorRowActions">
                <button
                  type="button"
                  onClick={() => move(i, -1)}
                  disabled={i === 0}
                  className="listEditorMoveBtn"
                >
                  ↑
                </button>
                <button
                  type="button"
                  onClick={() => move(i, 1)}
                  disabled={i === rows.length - 1}
                  className="listEditorMoveBtn"
                >
                  ↓
                </button>
                <button
                  type="button"
                  onClick={() => remove(i)}
                  data-testid={`cms-list-${section.key}-remove-${i}`}
                  className="listEditorRemoveBtn"
                >
                  Remove
                </button>
              </div>
            </div>
            <div className="gridTwoColGap3">
              {section.fields.map((f) => {
                const raw = row[f.key];
                const display =
                  f.asLines && Array.isArray(raw)
                    ? raw.join("\n")
                    : (raw ?? "");
                return (
                  <Field
                    key={f.key}
                    label={f.label}
                    value={display}
                    onChange={(v) => {
                      let nextValue = v;
                      if (f.asLines)
                        nextValue = String(v || "")
                          .split("\n")
                          .map((s) => s.trim())
                          .filter(Boolean);
                      else if (f.type === "number") nextValue = Number(v) || 0;
                      update(i, f.key, nextValue);
                    }}
                    type={f.type === "number" ? "number" : "text"}
                    textarea={!!f.textarea}
                    rows={f.rows}
                    placeholder={f.placeholder}
                    data-testid={`cms-list-${section.key}-${i}-${f.key}`}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SiteContentTab({ siteContent, setSiteContent }) {
  const [draft, setDraft] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setDraft(siteContent || {});
  }, [siteContent]);

  if (!siteContent) return <p className="contentLoadingText">Loading…</p>;

  const set = (k, v) => setDraft((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await http.put("/admin/site-content", draft);
      setSiteContent(data);
      setDraft(data);
      toast.success("Landing page content saved.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (key, file) => {
    if (!file) return;
    if (file.size > 3 * 1024 * 1024)
      return toast.error("Image must be under 3 MB.");
    const reader = new FileReader();
    reader.onload = async () => {
      const result = String(reader.result || "");
      const base64 = result.split(",")[1] || "";
      try {
        const { data } = await http.post("/admin/upload-image", {
          filename: file.name,
          content_type: file.type || "image/png",
          data_base64: base64,
        });
        set(key, data.url);
        toast.success("Image uploaded — remember to Save.");
      } catch (err) {
        toast.error(err?.response?.data?.detail || "Upload failed.");
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <div className="tabSpace8" data-testid="admin-content-tab">
      <div className="glass contentTextSection">
        <p className="overline contentTextOverline">Landing page · text</p>
        <p className="contentTextDesc">
          Edit the headline, sub-copy and overlines that appear across the
          landing page sections. Changes go live immediately after Save.
        </p>
        <div className="gridTwoCol">
          {TEXT_FIELDS.map(([k, label]) => (
            <Field
              key={k}
              label={label}
              value={draft[k]}
              onChange={(v) => set(k, v)}
              textarea={k.endsWith("_body")}
              rows={k.endsWith("_body") ? 3 : undefined}
              data-testid={`cms-input-${k}`}
            />
          ))}
        </div>
      </div>

      <div className="glass contentImagesSection">
        <p className="overline contentTextOverline">Landing page · images</p>
        <p className="contentTextDesc">
          Paste an image URL or upload a file (≤ 3 MB). Uploads are stored as
          data URLs.
        </p>
        <div className="contentImagesGrid">
          {IMAGE_FIELDS.map(([k, label]) => (
            <div key={k} className="contentImageCard">
              <span className="overline contentImageLabel">{label}</span>
              <input
                type="text"
                value={draft[k] || ""}
                onChange={(e) => set(k, e.target.value)}
                placeholder="https://… or paste a data URL"
                data-testid={`cms-image-url-${k}`}
                className="contentImageUrlInput"
              />
              <div className="contentImageUploadRow">
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => uploadImage(k, e.target.files?.[0])}
                  data-testid={`cms-image-upload-${k}`}
                  className="contentImageFileInput"
                />
                {draft[k] && (
                  <img
                    src={draft[k]}
                    alt=""
                    className="contentImagePreview"
                  />
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="glass contentTodaySection">
        <p className="overline contentTodayOverline">Today &amp; Tomorrow strip</p>
        <p className="contentTodayDesc">
          Maximum number of events to surface in the homepage &amp; landing
          &quot;happening today&quot; strip. Set up to 12 — the strip renders a
          responsive 3-column grid.
        </p>
        <Field
          label="today_max_events (1 – 12)"
          type="number"
          value={Number(draft.today_max_events ?? 6)}
          onChange={(v) =>
            set("today_max_events", Math.max(1, Math.min(12, Number(v) || 6)))
          }
          data-testid="cms-today-max-events"
        />
      </div>

      {LIST_SECTIONS.map((section) => (
        <ListEditor
          key={section.key}
          section={section}
          items={draft[section.key] || []}
          onChange={(rows) => set(section.key, rows)}
        />
      ))}

      <div className="contentSaveRow">
        <button
          onClick={save}
          disabled={saving}
          className="btn-gold"
          data-testid="cms-save-button"
        >
          <Save size={14} /> {saving ? "Saving…" : "Save changes"}
        </button>
        <TestEmailButton defaultTo="seshsuk@gmail.com" />
        <span className="contentSaveHint">
          Landing page reads these values from{" "}
          <code className="contentSaveHintCode">GET /api/site-content</code>
        </span>
      </div>
    </div>
  );
}

function TestEmailButton({ defaultTo }) {
  const [to, setTo] = useState(defaultTo || "");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);
  const [open, setOpen] = useState(false);

  const send = async () => {
    if (!to || !to.includes("@")) return toast.error("Enter a valid email.");
    setSending(true);
    try {
      const { data } = await http.post("/admin/test-email", { to, note });
      toast.success(
        data.delivered
          ? `Sent to ${data.to} via ${data.provider}.`
          : `Queued to outbox (Resend not configured). Outbox id: ${data.outbox_id?.slice(0, 8) || "—"}`,
      );
      setOpen(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed.");
    } finally {
      setSending(false);
    }
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="cms-test-email-toggle"
        className="testEmailToggle"
      >
        <Save size={12} /> Send test email
      </button>
    );
  }

  return (
    <div
      className="glass testEmailPanel"
      data-testid="cms-test-email-panel"
    >
      <div className="testEmailForm">
        <label className="testEmailToLabel">
          <span className="overline testEmailLabelText">Send to</span>
          <input
            type="email"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="cms-test-email-to"
            className="testEmailInput"
          />
        </label>
        <label className="testEmailNoteLabel">
          <span className="overline testEmailLabelText">Note (optional)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="QA pass · sponsor pricing"
            data-testid="cms-test-email-note"
            className="testEmailInput"
          />
        </label>
        <div className="testEmailActions">
          <button
            type="button"
            onClick={send}
            disabled={sending}
            data-testid="cms-test-email-send"
            className="btn-gold btnGoldXs"
          >
            {sending ? "Sending…" : "Send"}
          </button>
          <button
            type="button"
            onClick={() => setOpen(false)}
            data-testid="cms-test-email-close"
            className="testEmailCloseBtn"
          >
            Close
          </button>
        </div>
      </div>
      <p className="testEmailHint">
        Sends the live CMS hero/method/quote copy via the registration
        confirmation template. If{" "}
        <code className="testEmailHintCode">RESEND_API_KEY</code> isn't set, it
        queues to the email outbox.
      </p>
    </div>
  );
}

function SponsorRegistryTab({ registry, reload }) {
  const [running, setRunning] = useState(false);
  const [lastSweep, setLastSweep] = useState(null);

  const sweep = async () => {
    setRunning(true);
    try {
      const { data } = await http.post("/admin/sponsor-registry/sweep");
      setLastSweep(data);
      toast.success(
        `Sweep complete · ${data.notifications_sent} notification(s) sent across ${data.events_with_open_slots} open event(s).`,
      );
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Sweep failed.");
    } finally {
      setRunning(false);
    }
  };

  const remove = async (id, email) => {
    if (!confirm(`Permanently remove ${email} from the registry?`)) return;
    try {
      await http.delete(`/admin/sponsor-registry/${id}`);
      toast.success("Removed.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const buckets = {
    active: registry.filter((r) => r.status === "active" || !r.status),
    paused: registry.filter((r) => r.status === "paused"),
    unsubscribed: registry.filter((r) => r.status === "unsubscribed"),
  };

  return (
    <div className="tabSpace6" data-testid="admin-registry-tab">
      <div className="glass registrySweepCard">
        <div>
          <p className="overline registrySweepOverline">Daily sweep</p>
          <p className="registrySweepDesc">
            Matches active registry entries against events starting within 48 h
            that have no approved sponsor. Background loop runs every 24 h. Use
            this button to run an extra sweep on demand.
          </p>
          {lastSweep && (
            <p
              className="registrySweepResult"
              data-testid="admin-registry-last-sweep"
            >
              Last run:{" "}
              <span className="registrySweepHighlight">
                {lastSweep.notifications_sent}
              </span>{" "}
              notification(s) · {lastSweep.events_with_open_slots} event(s) with
              open slots · {new Date(lastSweep.ran_at).toLocaleString()}
            </p>
          )}
        </div>
        <button
          onClick={sweep}
          disabled={running}
          className="btn-gold"
          data-testid="admin-registry-sweep"
        >
          {running ? "Running…" : "Run sweep now"}
        </button>
      </div>

      <RegistryGroup
        title="Active"
        rows={buckets.active}
        onRemove={remove}
        testid="registry-active"
      />
      <RegistryGroup
        title="Paused"
        rows={buckets.paused}
        onRemove={remove}
        testid="registry-paused"
      />
      <RegistryGroup
        title="Unsubscribed"
        rows={buckets.unsubscribed}
        onRemove={remove}
        testid="registry-unsubscribed"
      />
    </div>
  );
}

function RegistryGroup({ title, rows, onRemove, testid }) {
  return (
    <div data-testid={`admin-${testid}`}>
      <p className="overline registryGroupLabel">
        {title} · {rows.length}
      </p>
      {rows.length === 0 ? (
        <p className="registryGroupEmpty">None.</p>
      ) : (
        <div className="registryGroupRows">
          {rows.map((r) => (
            <div
              key={r.id}
              className="glass registryRow"
              data-testid={`registry-row-${r.id}`}
            >
              <div className="registryRowInfo">
                <p className="registryRowCompany">
                  {r.company}
                </p>
                <p className="registryRowContact">
                  {r.contact_name} · {r.email}
                </p>
                <p className="registryRowInterests">
                  Interests:{" "}
                  {(r.interests || []).join(", ") || (
                    <em className="registryRowAnyPlaceholder">any</em>
                  )}{" "}
                  · Tiers:{" "}
                  {(r.tiers_interested || []).join(", ") || (
                    <em className="registryRowAnyPlaceholder">any</em>
                  )}
                </p>
                {r.value_prop && (
                  <p className="registryRowValueProp">
                    "{r.value_prop}"
                  </p>
                )}
                <p className="registryRowMeta">
                  Joined {new Date(r.created_at).toLocaleDateString()} ·
                  Notifications sent: {r.notifications_sent || 0}
                  {r.last_notified_at && (
                    <>
                      {" "}
                      · Last notified{" "}
                      {new Date(r.last_notified_at).toLocaleDateString()}
                    </>
                  )}
                  {r.paused_until && (
                    <>
                      {" "}
                      · Paused until{" "}
                      {new Date(r.paused_until).toLocaleDateString()}
                    </>
                  )}
                </p>
              </div>
              <button
                onClick={() => onRemove(r.id, r.email)}
                data-testid={`admin-registry-delete-${r.id}`}
                className="registryRemoveBtn"
              >
                <Trash2 size={12} /> Remove
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function VouchersTab({ vouchers, events, reload }) {
  const [form, setForm] = useState({
    event_id: events[0]?.id || "",
    sponsor_company: "",
    max_uses: 25,
    label: "",
    expires_at: "",
  });
  const [busy, setBusy] = useState(false);
  const eventTitle = (id) => events.find((e) => e.id === id)?.title || id;

  const create = async (e) => {
    e.preventDefault();
    if (!form.event_id || !form.sponsor_company)
      return toast.error("Pick an event and sponsor.");
    setBusy(true);
    try {
      const { data } = await http.post("/admin/vouchers", {
        ...form,
        max_uses: Number(form.max_uses),
      });
      toast.success(`Created ${data.code} for ${data.sponsor_company}.`);
      setForm({
        event_id: form.event_id,
        sponsor_company: "",
        max_uses: 25,
        label: "",
        expires_at: "",
      });
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Create failed.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this voucher? Past attributions stay intact.")) return;
    try {
      await http.delete(`/admin/vouchers/${id}`);
      toast.success("Voucher deleted.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Delete failed.");
    }
  };

  const copy = async (code) => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(`${code} copied.`);
    } catch {
      toast.error("Copy failed.");
    }
  };

  return (
    <div className="tabSpace6" data-testid="admin-vouchers-tab">
      <form
        onSubmit={create}
        className="glass voucherForm"
        data-testid="admin-voucher-form"
      >
        <div className="voucherFormHeader">
          <p className="overline voucherFormOverline">Create voucher</p>
          <p className="voucherFormDesc">
            Generate a one-off code a sponsor can hand out. Used codes attribute
            the attendee back to the sponsor automatically.
          </p>
        </div>
        <label className="voucherFormCol3">
          <span className="overline fieldLabel">Event</span>
          <select
            value={form.event_id}
            onChange={(e) => setForm({ ...form, event_id: e.target.value })}
            data-testid="admin-voucher-event"
            className="selectInput"
          >
            {events.map((ev) => (
              <option key={ev.id} value={ev.id}>
                {ev.title}
              </option>
            ))}
          </select>
        </label>
        <label className="voucherFormCol3">
          <span className="overline fieldLabel">Sponsor company</span>
          <input
            value={form.sponsor_company}
            onChange={(e) =>
              setForm({ ...form, sponsor_company: e.target.value })
            }
            data-testid="admin-voucher-sponsor"
            required
            className="fieldInput"
          />
        </label>
        <label className="voucherFormCol2">
          <span className="overline fieldLabel">Max uses</span>
          <input
            type="number"
            min={1}
            max={5000}
            value={form.max_uses}
            onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
            data-testid="admin-voucher-max-uses"
            className="fieldInput"
          />
        </label>
        <label className="voucherFormCol2">
          <span className="overline fieldLabel">Label (optional)</span>
          <input
            value={form.label}
            onChange={(e) => setForm({ ...form, label: e.target.value })}
            data-testid="admin-voucher-label"
            placeholder="Q2 guests"
            className="fieldInput"
          />
        </label>
        <div className="voucherFormColSubmit">
          <button
            type="submit"
            disabled={busy}
            data-testid="admin-voucher-create"
            className="btn-gold voucherFormSubmitBtn"
          >
            {busy ? "Creating…" : "Create"}
          </button>
        </div>
      </form>

      <div className="tabSpace3">
        <p className="overline voucherListOverline">All vouchers · {vouchers.length}</p>
        {vouchers.length === 0 ? (
          <p className="voucherListEmpty">No vouchers yet.</p>
        ) : (
          vouchers.map((v) => {
            const exhausted = v.used_count >= v.max_uses;
            return (
              <div
                key={v.id}
                data-testid={`admin-voucher-row-${v.id}`}
                className="glass voucherRow"
              >
                <div className="voucherRowInfo">
                  <div className="voucherRowHeader">
                    <button
                      onClick={() => copy(v.code)}
                      data-testid={`admin-voucher-copy-${v.id}`}
                      className="voucherCode"
                    >
                      {v.code}
                    </button>
                    <span
                      className={`voucherUsageBadge ${
                        exhausted
                          ? "voucherUsageBadgeExhausted"
                          : "voucherUsageBadgeActive"
                      }`}
                    >
                      {v.used_count}/{v.max_uses} used
                    </span>
                  </div>
                  <p className="voucherSponsor">
                    {v.sponsor_company}
                    <span className="voucherSponsorEvent">
                      {" "}
                      · {eventTitle(v.event_id)}
                    </span>
                  </p>
                  {v.label && (
                    <p className="voucherLabel">"{v.label}"</p>
                  )}
                  {v.attendees?.length > 0 && (
                    <div className="voucherAttendeesSection">
                      <p className="overline voucherAttendeesOverline">
                        Attendees from this voucher
                      </p>
                      <ul className="voucherAttendeesList">
                        {v.attendees.slice(0, 6).map((a, i) => (
                          <li key={i}>
                            {a.name} · {a.email}
                            {a.company ? ` · ${a.company}` : ""}
                          </li>
                        ))}
                        {v.attendees.length > 6 && (
                          <li className="voucherAttendeesMore">
                            + {v.attendees.length - 6} more
                          </li>
                        )}
                      </ul>
                    </div>
                  )}
                </div>
                <button
                  onClick={() => remove(v.id)}
                  data-testid={`admin-voucher-delete-${v.id}`}
                  className="voucherDeleteBtn"
                >
                  <Trash2 size={12} /> Delete
                </button>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function ExperimentsTab() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shipping, setShipping] = useState(false);

  const load = () => {
    setLoading(true);
    http
      .get("/admin/analytics/trending-ab")
      .then(({ data }) => setData(data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const ship = async (variant) => {
    setShipping(true);
    try {
      await http.post("/admin/experiments/trending/ship", { variant });
      load();
    } finally {
      setShipping(false);
    }
  };

  if (loading)
    return (
      <p className="experimentsLoadingText" data-testid="experiments-loading">
        Loading…
      </p>
    );
  if (!data)
    return (
      <p className="experimentsLoadingText" data-testid="experiments-empty">
        No data yet.
      </p>
    );

  const control = data.variants.control;
  const personalized = data.variants.personalized;
  const shipped = data.shipped || null;

  return (
    <div data-testid="experiments-tab" className="tabSpace8">
      <div className="experimentsHeader">
        <div>
          <h3 className="experimentsTitle">
            Trending strip · personalized vs control
          </h3>
          <p className="experimentsDesc">
            Every visitor is assigned 50/50 on first visit. "Personalized"
            reorders the rail to pin a card matching their browsing interests
            (with a "Made for you" pill). "Control" keeps the activity-only
            ranking. CTR = clicks ÷ impressions.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="experiments-refresh"
          className="experimentsRefreshBtn"
        >
          Refresh
        </button>
      </div>

      <div className="experimentsGrid">
        <ExperimentCard
          title="Control"
          tone="experimentCardBorderNeutral"
          stats={control}
          testId="experiments-control"
        />
        <ExperimentCard
          title="Personalized (Made for you)"
          tone="experimentCardBorderGold"
          stats={personalized}
          testId="experiments-personalized"
        />
      </div>

      <div
        data-testid="experiments-lift"
        className="experimentsLiftCard"
      >
        <p className="overline experimentsLiftOverline">Lift</p>
        {data.lift_pct === null ? (
          <p className="experimentsLiftWaiting">
            Waiting for enough control traffic to compute lift.
          </p>
        ) : (
          <p className="experimentsLiftValue">
            {data.lift_pct >= 0 ? "+" : ""}
            {data.lift_pct}%
            <span className="experimentsLiftDetail">
              ({personalized.ctr_pct}% vs {control.ctr_pct}% CTR)
            </span>
          </p>
        )}
      </div>

      <div
        data-testid="experiments-ship"
        className="experimentsShipCard"
      >
        <p className="overline experimentsShipOverline">Ship the winner</p>
        <p className="experimentsShipDesc">
          Override the 50/50 split and force every visitor into the chosen
          variant. Clearing the override resumes the experiment.
        </p>
        <p className="experimentsShipState" data-testid="experiments-ship-state">
          Currently shipped:{" "}
          {shipped ? (
            <span className="experimentsShipStateCurrent">{shipped}</span>
          ) : (
            <span className="experimentsShipStateNone">none — split is 50/50</span>
          )}
        </p>
        <div className="experimentsShipActions">
          <button
            onClick={() => ship("personalized")}
            disabled={shipping || shipped === "personalized"}
            data-testid="ship-personalized"
            className="shipPersonalizedBtn"
          >
            Ship personalized
          </button>
          <button
            onClick={() => ship("control")}
            disabled={shipping || shipped === "control"}
            data-testid="ship-control"
            className="shipControlBtn"
          >
            Ship control
          </button>
          {shipped && (
            <button
              onClick={() => ship("")}
              disabled={shipping}
              data-testid="ship-clear"
              className="shipClearBtn"
            >
              Clear override (resume 50/50)
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ExperimentCard({ title, tone, stats, testId }) {
  return (
    <div
      data-testid={testId}
      className={`experimentCard ${tone}`}
    >
      <p className="overline experimentCardOverline">{title}</p>
      <p
        className="experimentCardCtr"
        data-testid={`${testId}-ctr`}
      >
        {stats.ctr_pct}%<span className="experimentCardCtrUnit">CTR</span>
      </p>
      <div className="experimentCardStats">
        <Stat label="Impressions" value={stats.impressions} />
        <Stat label="Clicks" value={stats.clicks} />
        <Stat label="Unique clickers" value={stats.unique_clickers} />
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div>
      <p className="overline statLabel">{label}</p>
      <p className="statValue">{value}</p>
    </div>
  );
}